import type { ExternalToast } from "sonner";

import type { ReactNode } from "react";

import clsx from "clsx";
import { toast as sonnerToast, Toaster as SonnerToaster } from "sonner";

import { CloseIcon } from "../../icons/close";
import { ErrorGlyphIcon } from "../../icons/error-glyph";
import { InfoGlyphIcon } from "../../icons/info-glyph";
import { SuccessIcon } from "../../icons/success";
import { Button } from "../button";

import styles from "./toasts.module.css";

type ToastVariant = "error" | "info" | "success";

type ToastViewProps = {
	id: number | string;
	message: ReactNode;
	variant: ToastVariant;
};

function ToastIcon({ variant }: { variant: ToastVariant }): ReactNode {
	if (variant === "success") {
		return (
			<div className={clsx(styles.iconBadge, styles.iconBadgeSuccess)}>
				<SuccessIcon height={8} width={10} />
			</div>
		);
	}
	if (variant === "error") {
		return (
			<div className={clsx(styles.iconBadge, styles.iconBadgeError)}>
				<ErrorGlyphIcon height={10} width={10} />
			</div>
		);
	}
	return (
		<div className={clsx(styles.iconBadge, styles.iconBadgeInfo)}>
			<InfoGlyphIcon height={12} width={12} />
		</div>
	);
}

function ToastView({ id, message, variant }: ToastViewProps): ReactNode {
	return (
		<div className={styles.toast} data-testid={`toast-${variant}`}>
			<ToastIcon variant={variant} />
			<span className={clsx(styles.title, "body-2")}>{message}</span>
			<Button
				iconOnly
				onPress={() => {
					return sonnerToast.dismiss(id);
				}}
				size="sm"
				variant="primary"
			>
				<CloseIcon height={10} width={10} />
			</Button>
		</div>
	);
}

function renderToast(variant: ToastVariant) {
	return (message: ReactNode, options?: ExternalToast): number | string => {
		return sonnerToast.custom((id) => {
			return <ToastView id={id} message={message} variant={variant} />;
		}, options);
	};
}

export const toast = Object.assign(renderToast("info"), {
	dismiss: (id?: number | string): number | string | void => {
		return sonnerToast.dismiss(id);
	},
	error: renderToast("error"),
	info: renderToast("info"),
	success: renderToast("success"),
});

export function Toaster(): ReactNode {
	return (
		<SonnerToaster
			offset={24}
			position="bottom-left"
			toastOptions={{
				unstyled: true,
			}}
		/>
	);
}
